'use client';

import { motion } from 'framer-motion';
import { Sparkles, Briefcase, ArrowRight, Users, Rocket, Globe } from 'lucide-react';

const stats = [
  {
    icon: <Users className="w-6 h-6" />,
    value: '50+',
    label: 'Team Members',
  },
  {
    icon: <Globe className="w-6 h-6" />,
    value: '15+',
    label: 'Countries',
  },
  {
    icon: <Rocket className="w-6 h-6" />,
    value: '100%',
    label: 'Remote First',
  },
];

export function CareersHero() {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-background">
      {/* Premium Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Base grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(45deg,theme(colors.primary.DEFAULT/0.02)_1px,transparent_1px),linear-gradient(to_right,theme(colors.primary.DEFAULT/0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
        
        {/* Gradient orbs */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/5 to-primary-light/5 blur-[100px]"
            style={{
              top: `${20 + i * 30}%`,
              left: `${10 + i * 40}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
              rotate: [0, 45, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block"
          >
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary/10 text-primary border border-primary/20 shadow-lg shadow-primary/10"
            >
              <Briefcase className="w-4 h-4" />
              <span className="text-sm font-medium">We're Hiring!</span>
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8 text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.15]"
          >
            Join Our{' '}
            <span style={{ 
              display: 'inline-block',
              background: 'linear-gradient(90deg, #000000, #059669, #000000)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '200% auto',
              animation: 'textShimmer 2s linear infinite',
              paddingBottom: '0.1em',
              lineHeight: '1.3',
              verticalAlign: 'baseline'
            }}>
              Mission
            </span>{' '}
            <span className="inline-block">
              Together
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 text-xl sm:text-2xl lg:text-3xl max-w-3xl mx-auto text-muted-foreground leading-relaxed font-light"
          >
            Build the future of financial empowerment for service industry professionals
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10"
          >
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl 
                       bg-primary text-foreground font-medium text-lg
                       hover:shadow-xl hover:shadow-primary/20 
                       transition-all duration-300 group"
            >
              Join Our Marketing Team
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200" />
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="flex flex-col items-center gap-2 p-6 rounded-2xl
                         bg-background/50 backdrop-blur-sm border border-primary/10
                         hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5
                         transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20
                             flex items-center justify-center text-primary
                             group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <span className="text-3xl font-bold text-foreground">{stat.value}</span>
                <span className="text-muted-foreground">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Subtle Gradient Divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-foreground/[0.02]" />
    </section>
  );
} 